import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { SiteConfigManager, useApplicationState } from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import { NotificationTopic, TransportManager } from '@atlas/transport';

import { bonusStatuses, bonusTypes } from 'src/common/constants';
import useBonusesService from 'src/hooks/useBonusesService';

import {
  escapeRegExp,
  getBonusNotAllowedCountries,
  getBonusNotAllowedPaymentMethods,
  getMinDeposit,
  isAmountBelowMinDeposit,
  isCasinoBonus,
  isDepositBonus,
  markBonusAvailability,
  normalizePath,
  sortBonusesByAvailabilityAndAmount,
} from './utils';

const { DEPOSIT, RELOAD_DEPOSIT } = bonusTypes;
const { ACTIVATED, IN_PROGRESS } = bonusStatuses;
const CASINO_PRODUCT_TYPE = 'CASINO';
const SPORTSBOOK_PRODUCT_TYPE = 'SPORTSBOOK';

const PROVIDER_MODE = {
  DEPOSIT: 'deposit',
  QUICK: 'quick',
};
const EMPTY_BONUSES = [];
const createProviderModeState = defaultValue => ({
  [PROVIDER_MODE.DEPOSIT]: defaultValue,
  [PROVIDER_MODE.QUICK]: defaultValue,
});
const createProviderBonusState = () =>
  createProviderModeState({
    isManuallyDeselected: false,
    rememberedBonusId: null,
    selectedBonus: null,
  });

const BonusesContext = createContext({
  amountToGetBonus: 0,
  bonusesList: [],
  depositAmount: 0,
  freeSpinAsBonus: null,
  isLockedBonuses: false,
  paymentMethod: null,
  selectedBonus: null,
  setDepositAmount: () => {},
  setPaymentMethod: () => {},
  setSelectedBonus: () => {},
});

const BonusesProvider = ({ children }) => {
  const bonusesService = useBonusesService();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [bonusStateByProviderType, setBonusStateByProviderType] =
    useState(createProviderBonusState);
  const [deselectedActivatedBonusId, setDeselectedActivatedBonusId] = useState(null);
  const [pinnedBonusId, setPinnedBonusId] = useState(null);
  const [depositAmount, setDepositAmount] = useState(0);
  const [isLockedBonuses, setIsLockedBonuses] = useState(false);
  const activeBonuses = bonusesService?.clientBonuses ?? EMPTY_BONUSES;
  const availableBonuses = bonusesService?.bonuses ?? EMPTY_BONUSES;
  const applicationState = useApplicationState();
  const { country, currency } = applicationState.account;
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const casinoLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE);
  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);

  const { pathname, state: locationState } = useLocation();
  const previousShouldRefreshBonusesByRouteRef = useRef(false);

  const depositFreeSpin = locationState?.depositFreeSpin || null;

  const freeSpinAsBonus = useMemo(() => {
    if (!depositFreeSpin?.freeSpin) return null;

    const { bonusId, bonusInstanceId, freeSpin: freeSpinInstance } = depositFreeSpin;
    const { currency: freeSpinCurrency, expirationTime, freeSpin: freeSpinData } = freeSpinInstance;
    const {
      freeSpinMinAmountsDeposit,
      localizedImageInfoList = [],
      localizedInfoList = [],
    } = freeSpinData || {};
    const localizedInfo = localizedInfoList[0] || {};
    const minDeposit = freeSpinMinAmountsDeposit?.[currency] ?? 0;

    return {
      bonusId,
      bonusInstanceId,
      bonusType: 'FREESPIN',
      currency: freeSpinCurrency,
      disabled: false,
      endTime: expirationTime,
      expirationTime,
      financeData: { currency, minDeposit },
      financeDataList: [{ currency, minDeposit }],
      getImage: lang =>
        (
          localizedImageInfoList?.find(item => item.language === lang) ||
          localizedImageInfoList?.[0]
        )?.imageLink || '',
      id: `freespin-${bonusInstanceId}`,
      isFreeSpin: true,
      localizedImageInfoList,
      localizedInfoList,
      name: localizedInfo.name || 'Free Spin Bonus',
      notAllowedCountries: [],
      notAllowedPaymentMethods: [],
      productType: 'CASINO',
    };
  }, [currency, depositFreeSpin]);
  const depositBasePath = useMemo(
    () =>
      normalizePath(`${clientProfilePath}/${clientProfileRoutes.payment}/${paymentRoutes.deposit}`),
    [clientProfilePath],
  );

  const isDepositDetailsRoute = useMemo(() => {
    const normalizedPathname = normalizePath(pathname);
    const depositDetailsPattern = new RegExp(`^${escapeRegExp(depositBasePath)}/[^/]+$`);

    return depositDetailsPattern.test(normalizedPathname);
  }, [depositBasePath, pathname]);

  const shouldRefreshBonusesByRoute = useMemo(() => {
    return (
      pathname.includes(casinoPath) || pathname.includes(casinoLivePath) || isDepositDetailsRoute
    );
  }, [casinoLivePath, casinoPath, isDepositDetailsRoute, pathname]);

  const shouldUseQuickProviderMode = useMemo(() => {
    return pathname.includes(casinoPath) || pathname.includes(casinoLivePath);
  }, [casinoLivePath, casinoPath, pathname]);

  const providerType = useMemo(() => {
    return shouldUseQuickProviderMode ? PROVIDER_MODE.QUICK : PROVIDER_MODE.DEPOSIT;
  }, [shouldUseQuickProviderMode]);
  const { isManuallyDeselected, rememberedBonusId, selectedBonus } =
    bonusStateByProviderType[providerType];

  const setSelectedBonus = useCallback(
    nextSelectedBonus => {
      setBonusStateByProviderType(currentState => {
        const currentProviderState = currentState[providerType];
        const resolvedSelectedBonus =
          typeof nextSelectedBonus === 'function'
            ? nextSelectedBonus(currentProviderState.selectedBonus)
            : nextSelectedBonus;

        if (currentProviderState.selectedBonus === resolvedSelectedBonus) {
          return currentState;
        }

        return {
          ...currentState,
          [providerType]: {
            ...currentProviderState,
            selectedBonus: resolvedSelectedBonus,
          },
        };
      });
    },
    [providerType],
  );

  const setRememberedBonusId = useCallback(
    nextRememberedBonusId => {
      setBonusStateByProviderType(currentState => {
        const currentProviderState = currentState[providerType];

        if (currentProviderState.rememberedBonusId === nextRememberedBonusId) {
          return currentState;
        }

        return {
          ...currentState,
          [providerType]: {
            ...currentProviderState,
            rememberedBonusId: nextRememberedBonusId,
          },
        };
      });
    },
    [providerType],
  );

  const setIsManuallyDeselected = useCallback(
    isDeselected => {
      setBonusStateByProviderType(currentState => {
        const currentProviderState = currentState[providerType];

        if (currentProviderState.isManuallyDeselected === isDeselected) {
          return currentState;
        }

        return {
          ...currentState,
          [providerType]: {
            ...currentProviderState,
            isManuallyDeselected: isDeselected,
          },
        };
      });
    },
    [providerType],
  );

  const refreshBonuses = useCallback(async () => {
    if (!bonusesService) {
      return;
    }

    await bonusesService.load();
  }, [bonusesService]);

  useEffect(() => {
    refreshBonuses();
  }, [refreshBonuses]);

  useEffect(() => {
    const unsubscribe = TransportManager.getInstance().subscribe(
      { notificationTopic: NotificationTopic.BONUS_INSTANCE_STATUS_CHANGED },
      ({ payload }) => {
        setPinnedBonusId(payload?.data?.bonusId);
      },
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!previousShouldRefreshBonusesByRouteRef.current && shouldRefreshBonusesByRoute) {
      refreshBonuses();
    }

    previousShouldRefreshBonusesByRouteRef.current = shouldRefreshBonusesByRoute;
  }, [refreshBonuses, shouldRefreshBonusesByRoute]);

  useEffect(() => {
    if (!paymentMethod?.id) {
      return;
    }

    setBonusStateByProviderType(currentState => ({
      [PROVIDER_MODE.DEPOSIT]: {
        ...currentState[PROVIDER_MODE.DEPOSIT],
        isManuallyDeselected: false,
      },
      [PROVIDER_MODE.QUICK]: {
        ...currentState[PROVIDER_MODE.QUICK],
        isManuallyDeselected: false,
      },
    }));
    setPinnedBonusId(null);
    refreshBonuses();
  }, [paymentMethod?.id, refreshBonuses]);

  const casinoBonuses = useMemo(
    () =>
      availableBonuses.filter(
        bonus =>
          isCasinoBonus(bonus.productType, CASINO_PRODUCT_TYPE) &&
          isDepositBonus(bonus.type, [DEPOSIT, RELOAD_DEPOSIT]),
      ),
    [availableBonuses],
  );

  const isBonusAllowed = useCallback(
    bonus =>
      !getBonusNotAllowedPaymentMethods(bonus).map(String).includes(String(paymentMethod?.id)) &&
      !getBonusNotAllowedCountries(bonus).includes(country?.toLowerCase()),
    [country, paymentMethod?.id],
  );
  const isSelectableBonus = useCallback(
    bonus => {
      if (!bonus || bonus.disabled || !isBonusAllowed(bonus)) {
        return false;
      }

      return !isAmountBelowMinDeposit(depositAmount, getMinDeposit(bonus, currency));
    },
    [currency, depositAmount, isBonusAllowed],
  );
  const { activatedBonus, inProgressBonuses, quickInProgressBonuses, sportsbookActivatedBonus } =
    useMemo(() => {
      const activatedBonus =
        activeBonuses
          .map(bonus => markBonusAvailability(bonus, isBonusAllowed))
          .find(bonus => bonus.status === ACTIVATED) ?? null;
      const inProgressBonuses = activeBonuses
        .map(bonus => markBonusAvailability(bonus, isBonusAllowed))
        .filter(bonus => bonus.status === IN_PROGRESS);
      const quickActivatedBonus = activeBonuses
        .map(bonus => markBonusAvailability(bonus, isBonusAllowed))
        .find(
          bonus =>
            bonus.status === ACTIVATED && isCasinoBonus(bonus.productType, CASINO_PRODUCT_TYPE),
        );
      const quickInProgressBonuses = inProgressBonuses.filter(bonus =>
        isCasinoBonus(bonus.productType, CASINO_PRODUCT_TYPE),
      );
      const sportsbookActivatedBonus = activeBonuses
        .map(bonus => markBonusAvailability(bonus, isBonusAllowed))
        .find(bonus => bonus.status === ACTIVATED && bonus.productType === SPORTSBOOK_PRODUCT_TYPE);

      return {
        activatedBonus,
        inProgressBonuses,
        quickActivatedBonus,
        quickInProgressBonuses,
        sportsbookActivatedBonus,
      };
    }, [activeBonuses, isBonusAllowed]);
  const isActivatedBonusDeselected = activatedBonus?.id === deselectedActivatedBonusId;

  const quickDepositBonuses = useMemo(
    () =>
      casinoBonuses
        .map(bonus => markBonusAvailability(bonus, isBonusAllowed))
        .sort(sortBonusesByAvailabilityAndAmount),
    [casinoBonuses, isBonusAllowed],
  );

  const rememberedBonus = useMemo(() => {
    const bonusesSource =
      providerType === PROVIDER_MODE.QUICK ? quickDepositBonuses : availableBonuses;

    return bonusesSource.find(bonus => bonus.id === rememberedBonusId) ?? null;
  }, [availableBonuses, providerType, quickDepositBonuses, rememberedBonusId]);

  const pinnedBonus = useMemo(() => {
    if (pinnedBonusId === null || pinnedBonusId === undefined) {
      return null;
    }

    const bonusesSource =
      providerType === PROVIDER_MODE.QUICK ? quickDepositBonuses : availableBonuses;

    return bonusesSource.find(bonus => String(bonus.id) === String(pinnedBonusId)) ?? null;
  }, [availableBonuses, pinnedBonusId, providerType, quickDepositBonuses]);

  const amountToGetBonus = useMemo(() => {
    const amount = Number(depositAmount);
    if (!depositAmount || Number.isNaN(amount)) return 0;

    const bonus = selectedBonus || pinnedBonus || rememberedBonus;
    if (!bonus || bonus.disabled || !isBonusAllowed(bonus)) return 0;

    const minDeposit = getMinDeposit(bonus, currency);

    return Math.max(minDeposit - amount, 0);
  }, [currency, depositAmount, isBonusAllowed, pinnedBonus, rememberedBonus, selectedBonus]);

  const makeQuickDepositBonuses = useCallback(() => {
    if (quickInProgressBonuses.length > 0) {
      return [];
    }

    return quickDepositBonuses;
  }, [quickInProgressBonuses, quickDepositBonuses]);

  const makeDepositBonuses = useCallback(() => {
    const filteredBonuses = availableBonuses
      .filter(bonus => isDepositBonus(bonus.type, [DEPOSIT, RELOAD_DEPOSIT]))
      .map(bonus => markBonusAvailability(bonus, isBonusAllowed));
    if (inProgressBonuses.length > 0) {
      const inProgressProductTypes = new Set(
        inProgressBonuses.map(bonus => bonus.productType).filter(Boolean),
      );

      return filteredBonuses
        .filter(bonus => !inProgressProductTypes.has(bonus.productType))
        .sort(sortBonusesByAvailabilityAndAmount);
    }

    return filteredBonuses.sort(sortBonusesByAvailabilityAndAmount);
  }, [availableBonuses, inProgressBonuses, isBonusAllowed]);

  const bonusesList = useMemo(() => {
    if (freeSpinAsBonus) {
      return [freeSpinAsBonus];
    }

    if (providerType === PROVIDER_MODE.QUICK && sportsbookActivatedBonus) {
      return [];
    }

    if (activatedBonus) {
      return [activatedBonus];
    }

    if (pinnedBonus) {
      return [pinnedBonus];
    }

    if (providerType === PROVIDER_MODE.QUICK) {
      return makeQuickDepositBonuses();
    }

    return makeDepositBonuses();
  }, [
    activatedBonus,
    freeSpinAsBonus,
    makeDepositBonuses,
    makeQuickDepositBonuses,
    pinnedBonus,
    providerType,
    sportsbookActivatedBonus,
  ]);

  useEffect(() => {
    if (freeSpinAsBonus) {
      setIsLockedBonuses(true);

      if (selectedBonus?.id !== freeSpinAsBonus.id) {
        setSelectedBonus(freeSpinAsBonus);
      }

      return;
    }

    if (providerType === PROVIDER_MODE.QUICK && sportsbookActivatedBonus) {
      setIsLockedBonuses(false);

      if (selectedBonus !== null) {
        setSelectedBonus(null);
      }

      return;
    }

    if (activatedBonus) {
      setIsLockedBonuses(!isActivatedBonusDeselected);

      if (isActivatedBonusDeselected) {
        if (selectedBonus !== null) {
          setSelectedBonus(null);
        }

        return;
      }

      if (selectedBonus?.id !== activatedBonus.id || selectedBonus !== activatedBonus) {
        setSelectedBonus(activatedBonus);
      }

      return;
    }

    if (pinnedBonus) {
      setIsLockedBonuses(true);

      if (selectedBonus?.id !== pinnedBonus.id || selectedBonus !== pinnedBonus) {
        setSelectedBonus(pinnedBonus);
      }

      return;
    }

    if (providerType === PROVIDER_MODE.QUICK) {
      if (quickInProgressBonuses.length > 0) {
        setIsLockedBonuses(false);
        setSelectedBonus(null);
        return;
      }

      setIsLockedBonuses(false);

      const currentSelectedBonus =
        quickDepositBonuses.find(bonus => bonus.id === selectedBonus?.id) ?? null;
      const syncedBonuses = [currentSelectedBonus, rememberedBonus]
        .filter(Boolean)
        .map(({ id }) => quickDepositBonuses.find(bonus => bonus.id === id))
        .filter(Boolean);
      const selectableBonus = syncedBonuses.find(isSelectableBonus);
      const hasBonusBelowMinDeposit = syncedBonuses.some(bonus =>
        isAmountBelowMinDeposit(depositAmount, getMinDeposit(bonus, currency)),
      );
      const firstAvailableBonus = quickDepositBonuses.find(isSelectableBonus) ?? null;
      const nextBonus =
        selectableBonus ||
        (!hasBonusBelowMinDeposit && !isManuallyDeselected ? firstAvailableBonus : null);

      if (!nextBonus) {
        setSelectedBonus(null);
        return;
      }

      if (rememberedBonusId !== nextBonus.id) {
        setRememberedBonusId(nextBonus.id);
      }

      if (selectedBonus?.id !== nextBonus.id || selectedBonus !== nextBonus) {
        setSelectedBonus(nextBonus);
      }
    }
  }, [
    activatedBonus,
    bonusesList,
    currency,
    depositAmount,
    freeSpinAsBonus,
    isActivatedBonusDeselected,
    isSelectableBonus,
    isManuallyDeselected,
    providerType,
    quickInProgressBonuses,
    quickDepositBonuses,
    pinnedBonus,
    rememberedBonus,
    rememberedBonusId,
    selectedBonus,
    setRememberedBonusId,
    setSelectedBonus,
    sportsbookActivatedBonus,
  ]);

  useEffect(() => {
    if (providerType !== PROVIDER_MODE.DEPOSIT || !selectedBonus) {
      return;
    }

    setRememberedBonusId(selectedBonus.id);
  }, [providerType, selectedBonus, setRememberedBonusId]);

  useEffect(() => {
    if (
      providerType !== PROVIDER_MODE.DEPOSIT ||
      activatedBonus ||
      freeSpinAsBonus ||
      pinnedBonus ||
      sportsbookActivatedBonus
    ) {
      return;
    }

    setIsLockedBonuses(false);
    setSelectedBonus(currentSelectedBonus => {
      const syncedBonuses = [currentSelectedBonus, rememberedBonus]
        .filter(Boolean)
        .map(({ id }) => bonusesList.find(bonus => bonus.id === id))
        .filter(Boolean);
      const selectableBonus = syncedBonuses.find(isSelectableBonus);
      const hasBonusBelowMinDeposit = syncedBonuses.some(bonus =>
        isAmountBelowMinDeposit(depositAmount, getMinDeposit(bonus, currency)),
      );

      if (selectableBonus) {
        return selectableBonus;
      }

      if (hasBonusBelowMinDeposit || isManuallyDeselected) {
        return null;
      }

      return bonusesList.find(isSelectableBonus) ?? null;
    });
  }, [
    activatedBonus,
    bonusesList,
    currency,
    depositAmount,
    freeSpinAsBonus,
    isSelectableBonus,
    isManuallyDeselected,
    pinnedBonus,
    providerType,
    rememberedBonus,
    setSelectedBonus,
    sportsbookActivatedBonus,
  ]);

  const handleSetSelectedBonus = useCallback(
    bonus => {
      if (!bonus) {
        if (selectedBonus?.status === ACTIVATED) {
          setDeselectedActivatedBonusId(selectedBonus.id);
        }

        setIsManuallyDeselected(true);
        setRememberedBonusId(null);
        setSelectedBonus(null);
        return;
      }

      if (bonus.status === ACTIVATED) {
        setDeselectedActivatedBonusId(null);
      }

      setIsManuallyDeselected(false);
      setRememberedBonusId(bonus.id);
      setSelectedBonus(bonus);
    },
    [selectedBonus, setIsManuallyDeselected, setRememberedBonusId, setSelectedBonus],
  );

  const value = useMemo(
    () => ({
      activeBonuses,
      amountToGetBonus,
      bonusesList,
      depositAmount,
      freeSpinAsBonus,
      isLockedBonuses,
      paymentMethod,
      selectedBonus,
      setDepositAmount,
      setPaymentMethod,
      setSelectedBonus: handleSetSelectedBonus,
    }),
    [
      depositAmount,
      activeBonuses,
      amountToGetBonus,
      bonusesList,
      freeSpinAsBonus,
      handleSetSelectedBonus,
      isLockedBonuses,
      paymentMethod,
      selectedBonus,
    ],
  );
  return <BonusesContext.Provider value={value}>{children}</BonusesContext.Provider>;
};

BonusesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useBonusesContext = () => useContext(BonusesContext);
export default BonusesProvider;
